import React, { useState, useEffect, useMemo } from 'react';
import { Button, Modal, Spinner, Toast, Alert } from 'react-bootstrap';
import { CircularProgress, Box } from '@mui/material';

const RevisionHomepage = () => {
  const [formularios, setFormularios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userInfo, setUserInfo] = useState(null);
  const [mensajeEvaluacion, setMensajeEvaluacion] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [descargandoPdf, setDescargandoPdf] = useState(new Set());
  const [enviandoCorreo, setEnviandoCorreo] = useState(new Set());
  const [searchText, setSearchText] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [filterEstado, setFilterEstado] = useState('');

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const resUser = await fetch('https://apiprov.conduto.com.ec/api/protected-cookie', { credentials: 'include' });
      if (resUser.ok) setUserInfo((await resUser.json()).usuario);

      const resFormularios = await fetch('https://apiprov.conduto.com.ec/api/formularios/notificados', { credentials: 'include' });
      if (!resFormularios.ok) throw new Error('Error al cargar proveedores calificados');

      setFormularios(await resFormularios.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarDatos();
  }, []);

  const enviarCorreoSubido = async (idFormulario) => {
    if (!idFormulario) return;
    setEnviandoCorreo(prev => new Set(prev).add(idFormulario));
    setMensajeEvaluacion('');
    try {
      const res = await fetch(`https://apiprov.conduto.com.ec/api/formularios/${idFormulario}/notificar-subido`, {
        method: 'POST',
        credentials: 'include'
      });

      if (res.ok) {

        setFormularios(prev => prev.map(f =>
          f.id_formulario === idFormulario ? {
            ...f,
            evaluado: true,
            subido: 1
          } : f
        ));

        setMensajeEvaluacion('Correo enviado con éxito y formulario marcado como evaluado.');
      }
    } catch (e) {
      setMensajeEvaluacion('Error al enviar correo.');
    } finally {
      setEnviandoCorreo(prev => {
        const n = new Set(prev);
        n.delete(idFormulario);
        return n;
      });
      setShowToast(true);
    }
  };

  const abrirArchivoPorTitulo = async (idFormulario, tituloPregunta) => {
    try {
      const res = await fetch(
        `https://apiprov.conduto.com.ec/api/documentos/descargar-archivo/${idFormulario}?titulo=${encodeURIComponent(tituloPregunta)}`,
        { credentials: 'include' }
      );

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || `Error ${res.status}: No se pudo obtener el archivo`);
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (e) {
      console.error('Error al abrir archivo:', e);
      alert(`No se pudo abrir el archivo: ${e.message}`);
    }
  };

  const descargarPdf = async (idFormulario) => {
    if (!idFormulario) return;
    setDescargandoPdf(prev => new Set(prev).add(idFormulario));
    try {
      const res = await fetch(`https://apiprov.conduto.com.ec/api/providers/descargar-resultado/${idFormulario}`, { method: 'GET', credentials: 'include' });
      if (!res.ok) throw new Error('No se pudo abrir el PDF');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setMensajeEvaluacion('Error al abrir el PDF.');
      setShowToast(true);
    } finally {
      setDescargandoPdf(prev => { const n = new Set(prev); n.delete(idFormulario); return n; });
    }
  };

  const getEstadoBadge = (estado) => {
    const map = { activo: 'bg-success', pendiente: 'bg-warning text-dark', finalizado: 'bg-info', cancelado: 'bg-secondary', incompleto: 'bg-danger' };
    return <span className={`badge rounded-pill ${map[estado] || 'bg-secondary'}`}>{estado}</span>;
  };

  const getTipoPersonaBadge = (tipo) => (
    <span className={`badge rounded-pill ${tipo === 'juridica' ? 'bg-primary' : 'bg-secondary'}`}>{tipo}</span>
  );

  const filtered = useMemo(() => {
    return formularios.filter(f => {
      const coincideTexto = searchText === '' ||
        f.titulo?.toLowerCase().includes(searchText.toLowerCase()) ||
        f.usuario?.nombre?.toLowerCase().includes(searchText.toLowerCase()) ||
        f.usuario?.correo?.toLowerCase().includes(searchText.toLowerCase());
      const coincideTipo = filterTipo === '' || f.tipo_persona === filterTipo;
      const coincideEstado = filterEstado === '' || f.estado === filterEstado;
      return coincideTexto && coincideTipo && coincideEstado;
    });
  }, [formularios, searchText, filterTipo, filterEstado]);

  if (loading) return <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>;
  if (error) return <div className="container mt-4"><Alert variant="danger"><Alert.Heading>Error</Alert.Heading><p>{error}</p></Alert></div>;

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="mb-0" style={{ color: '#005793' }}>Proveedores Calificados</h3>
        <Button variant="outline-primary" onClick={cargarDatos} title="Recargar datos de la tabla">
          <i class="bi bi-arrow-clockwise"></i>
        </Button>
      </div>

      <div className="bg-white rounded shadow-sm p-3 mb-3">
        <div className="row g-2">
          <div className="col-md-5">
            <input type="text" className="form-control" placeholder="Buscar por título, proveedor o email..." value={searchText} onChange={e => setSearchText(e.target.value)} />
          </div>
          <div className="col-md-3">
            <select className="form-select" value={filterTipo} onChange={e => setFilterTipo(e.target.value)}>
              <option value="">Todos los tipos</option>
              <option value="natural">Natural</option>
              <option value="juridica">Jurídica</option>
            </select>
          </div>
          <div className="col-md-3">
            <select className="form-select" value={filterEstado} onChange={e => setFilterEstado(e.target.value)}>
              <option value="">Todos los estados</option>
              <option value="activo">Activo</option>
              <option value="finalizado">Finalizado</option>
            </select>
          </div>
          <div className="col-md-1 d-grid">
            <Button variant="outline-secondary" onClick={() => { setSearchText(''); setFilterTipo(''); setFilterEstado(''); }}>Limpiar</Button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded shadow-sm p-3">
        <h5 className="border-bottom pb-2 mb-3">Listado</h5>
        {filtered.length === 0 ? <p className="text-muted">No hay proveedores calificados</p> : (
          <div className="table-responsive" style={{ maxHeight: 400, overflow: 'auto' }}>
            <table className="table table-hover align-middle">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Proveedor</th>
                  <th>Email</th>
                  <th>Tipo</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(form => (
                  <tr key={form.id_formulario}>
                    <td className="fw-medium">{form.id_formulario}</td>
                    <td>
                      {form.usuario ? <>
                        <div className="fw-medium">{form.usuario.nombre}</div>
                        <small className="text-muted">@{form.usuario.usuario}</small>
                      </> : <span className="text-muted">—</span>}
                    </td>
                    <td className="text-muted">{form.usuario?.correo || '—'}</td>
                    <td>{getTipoPersonaBadge(form.usuario?.tipo_persona)}</td>
                    <td>{getEstadoBadge(form.estado)}</td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button size="sm" variant="outline-secondary" title="Certificados bancarios actualizados" onClick={() => abrirArchivoPorTitulo(form.id_formulario, 'Certificados bancarios actualizados')}>Banco</Button>
                        <Button size="sm" variant="outline-secondary" title="Copia simple del RUC actualizado" onClick={() => abrirArchivoPorTitulo(form.id_formulario, 'Copia simple del RUC actualizado')}>RUC</Button>
                        <Button size="sm" variant="outline-primary" title="Abrir Resultado PDF" onClick={() => descargarPdf(form.id_formulario)} disabled={descargandoPdf.has(form.id_formulario)}>
                          {descargandoPdf.has(form.id_formulario) ? <Spinner animation="border" size="sm" /> : 'PDF'}
                        </Button>
                      </div>
                    </td>
                    <td>
                      <Button size="sm" variant={form.subido === 1 || form.subido === true ? "success" : "outline-success"} title="Enviar notificación de subido" onClick={() => enviarCorreoSubido(form.id_formulario)} disabled={enviandoCorreo.has(form.id_formulario)}>
                        {enviandoCorreo.has(form.id_formulario) ? <Spinner animation="border" size="sm" /> : (form.subido === 1 || form.subido === true ? 'Subido' : 'Subir')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Toast show={showToast} onClose={() => setShowToast(false)} delay={4000} autohide className="position-fixed bottom-0 end-0 m-3">
        <Toast.Body>{mensajeEvaluacion}</Toast.Body>
      </Toast>
    </div>
  );
};

export default RevisionHomepage;